import { useRef } from 'react';
import { streamAsk, type HistoryTurn } from '../api/askApi';
import { applyEvent, isRunning, newTurn, type Turn } from '../model/conversation';
import { useReportChatStore } from '../model/reportChatStore';

const HISTORY_TURNS = 3;
const NO_TURNS: Turn[] = [];

/** Ask on a report page: questions answered from that report alone, in a conversation of its own. */
export function useReportConversation(reportId: string) {
  const turns = useReportChatStore((s) => s.byReport[reportId] ?? NO_TURNS);
  const current = useRef<{ controller: AbortController; turnId: string } | null>(null);
  const { add, update } = useReportChatStore.getState();

  const stop = () => {
    if (!current.current) return;
    const { controller, turnId } = current.current;
    controller.abort();
    current.current = null;
    update(reportId, turnId, (t) => (isRunning(t) ? { ...t, stopped: true } : t));
  };

  const send = (question: string) => {
    stop();
    const history: HistoryTurn[] = turns
      .filter((t) => t.answer?.status === 'answered')
      .slice(-HISTORY_TURNS)
      .map((t) => ({ question: t.question, answer: t.answer?.text ?? '' }));
    const turn = newTurn(question);
    add(reportId, turn);
    const controller = new AbortController();
    current.current = { controller, turnId: turn.id };
    const patch = (fn: (t: Turn) => Turn) => { update(reportId, turn.id, fn); };

    streamAsk({ question, history, documentId: reportId }, (evt) => { patch((t) => applyEvent(t, evt)); }, controller.signal)
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        patch((t) => ({ ...t, error: err instanceof Error ? err.message : 'The assistant is unavailable.' }));
      })
      .finally(() => { if (current.current?.turnId === turn.id) current.current = null; });
  };

  return { turns, send, stop, isBusy: turns.some(isRunning) };
}
